import React, { useState } from 'react';
import { JsonView } from 'react-json-view-lite';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { Button } from '../../ui/Button.js';
import { AutoRenderer } from './AutoRenderer.js';
import { ConcernsList } from './ConcernsList.js';
import { OutputPartsSection } from './OutputPartsSection.js';

/** Custom theme — matches RichContentRenderer's jsonTheme */
const jsonTheme = {
  container: 'jv-container text-sm font-mono',
  basicChildStyle: 'jv-child',
  label: 'jv-label text-accent-text',
  clickableLabel: 'jv-label jv-clickable text-accent-text',
  nullValue: 'text-faint italic',
  undefinedValue: 'text-faint italic',
  stringValue: 'text-body',
  booleanValue: 'text-warning',
  numberValue: 'text-accent-text',
  otherValue: 'text-body',
  punctuation: 'jv-punctuation text-faint',
  expandIcon: 'jv-expand text-muted cursor-pointer inline-block',
  collapseIcon: 'jv-collapse text-muted cursor-pointer inline-block',
  collapsedContent: 'jv-collapsed text-faint cursor-pointer',
  noQuotesForStringValues: false,
};

// ── Status badge colors ─────────────────────────────────────────

const statusStyles: Record<string, string> = {
  completed: 'bg-success-muted text-success',
  done: 'bg-success-muted text-success',
  done_with_concerns: 'bg-warning-muted text-warning',
  failed: 'bg-danger-muted text-danger',
  error: 'bg-danger-muted text-danger',
  running: 'bg-accent/20 text-accent-text',
  pending: 'bg-surface-alt text-muted',
};

function getStatusStyle(status: string): string {
  const key = status.toLowerCase().replace(/[\s-]/g, '_');
  return statusStyles[key] ?? 'bg-surface-alt text-body';
}

// ── Detection helper ────────────────────────────────────────────

export function isAgentStepOutput(data: unknown): boolean {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;
  const obj = data as Record<string, unknown>;
  if (Array.isArray(obj.outputParts)) return true;
  if (typeof obj.status === 'string' && typeof obj.confidence === 'number') return true;
  if (typeof obj.output === 'object' && obj.output !== null && !Array.isArray(obj.output)) return true;
  return false;
}

// ── Status Header ───────────────────────────────────────────────

const StatusHeader: React.FC<{ status: string; confidence: number }> = ({ status, confidence }) => (
  <div className="flex items-center justify-between bg-surface rounded-lg border border p-3">
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold">Status</span>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-sm ${getStatusStyle(status)}`}>
        {status.toUpperCase().replace(/_/g, ' ')}
      </span>
    </div>
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted">Confidence</span>
      <div className="w-24 h-2.5 bg-surface-alt rounded-full overflow-hidden">
        <div
          className="h-full bg-accent rounded-full transition-all"
          style={{ width: `${Math.min(Math.max(confidence * 100, 0), 100)}%` }}
        />
      </div>
      <span className="text-xs font-mono text-body">{(confidence * 100).toFixed(0)}%</span>
    </div>
  </div>
);

// ── Section divider ─────────────────────────────────────────────

const SectionDivider: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex items-center gap-2 pt-2">
    <span className="text-xs font-semibold text-muted uppercase tracking-wide">{title}</span>
    <div className="flex-1 h-px bg-surface-alt" />
  </div>
);

// ── Raw JSON toggle ─────────────────────────────────────────────

const RawJsonSection: React.FC<{ data: unknown }> = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="rounded-lg border border overflow-hidden">
      <div className="flex items-center justify-between bg-surface-alt pr-2">
        <Button variant="ghost" size="sm" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
          Raw JSON
        </Button>
        {open && (
          <Button variant="ghost" size="sm" iconOnly onClick={handleCopy} aria-label="Copy JSON">
            {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
          </Button>
        )}
      </div>
      {open && (
        <div className="p-2 bg-bg overflow-auto max-h-[600px]">
          <JsonView data={data as object} shouldExpandNode={(level) => level < 2} style={jsonTheme} />
        </div>
      )}
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────

interface StepOutputViewerProps {
  data: Record<string, unknown>;
}

export const StepOutputViewer: React.FC<StepOutputViewerProps> = ({ data }) => {
  const hasStatus = typeof data.status === 'string' && typeof data.confidence === 'number';
  const hasOutput = typeof data.output === 'object' && data.output !== null && !Array.isArray(data.output);
  const hasConcerns =
    Array.isArray(data.concerns) &&
    data.concerns.length > 0 &&
    data.concerns.every((c: unknown) => typeof c === 'string');
  const hasOutputParts = Array.isArray(data.outputParts);

  return (
    <div className="space-y-3">
      {/* Status header */}
      {hasStatus && <StatusHeader status={data.status as string} confidence={data.confidence as number} />}

      {/* Main output section */}
      {hasOutput && (
        <>
          <SectionDivider title="Step Output" />
          <div className="bg-surface rounded-lg border border p-4">
            <AutoRenderer data={data.output} depth={0} />
          </div>
        </>
      )}

      {/* Concerns */}
      {hasConcerns && (
        <>
          <SectionDivider title="Concerns" />
          <ConcernsList concerns={data.concerns as string[]} />
        </>
      )}

      {/* Output parts */}
      {hasOutputParts && (
        <>
          <SectionDivider title="Output Parts" />
          <OutputPartsSection parts={data.outputParts as Array<Record<string, unknown>>} />
        </>
      )}

      {/* Raw JSON fallback */}
      <RawJsonSection data={data} />
    </div>
  );
};
